import { c as _c } from "react/compiler-runtime";
import { Button } from '@repo/ui/components';
import { ExternalLink, type LucideIcon } from '@repo/ui/icons';
import type { ComponentProps } from 'react';
import { Link } from '../adapters/next';
import { cn } from '@repo/ui/lib/utils';
type GoogleMapsDrop = {
  place?: {
    lat?: null | number | string;
    lng?: null | number | string;
    place_id?: null | string;
  };
};
type GoogleMapsLinkProps = Pick<ComponentProps<typeof Button>, 'className' | 'size' | 'variant'> & {
  drops: GoogleMapsDrop[];
  icon?: LucideIcon;
  label?: string;
};
export const GoogleMapsLink = t0 => {
  const $ = _c(25);
  const {
    className,
    drops,
    icon: t1,
    label,
    size: t2,
    variant: t3
  } = t0;
  const Icon = t1 === undefined ? ExternalLink : t1;
  const size = t2 === undefined ? "icon" : t2;
  const variant = t3 === undefined ? "ghost" : t3;
  let t4;
  if ($[0] !== drops) {
    t4 = getGoogleMapsUrl(drops);
    $[0] = drops;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const href = t4;
  if (!href) {
    let t5;
    if ($[2] !== className) {
      t5 = cn(className);
      $[2] = className;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== Icon) {
      t6 = <Icon size={16} />;
      $[4] = Icon;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== label || $[7] !== size || $[8] !== t5 || $[9] !== t6 || $[10] !== variant) {
      t7 = <Button className={t5} disabled={true} size={size} variant={variant}>{t6}{label}</Button>;
      $[6] = label;
      $[7] = size;
      $[8] = t5;
      $[9] = t6;
      $[10] = variant;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t5;
  if ($[12] !== className) {
    t5 = cn(className);
    $[12] = className;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== href) {
    t6 = <Link href={href} rel="noreferrer" target="_blank" />;
    $[14] = href;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== Icon) {
    t7 = <Icon size={16} data-sentry-element="Icon" data-sentry-source-file="google-maps-link.tsx" />;
    $[16] = Icon;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== label || $[19] !== size || $[20] !== t5 || $[21] !== t6 || $[22] !== t7 || $[23] !== variant) {
    t8 = <Button className={t5} render={t6} size={size} variant={variant} data-sentry-element="Button" data-sentry-component="GoogleMapsLink" data-sentry-source-file="google-maps-link.tsx">{t7}{label}</Button>;
    $[18] = label;
    $[19] = size;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = variant;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};
const getGoogleMapsUrl = (drops: GoogleMapsDrop[]) => {
  const routePoints = drops.flatMap(drop => {
    const coordinates = getDropCoordinates(drop);
    if (!coordinates) {
      return [];
    }
    return [{
      coordinates,
      placeId: drop.place?.place_id
    }];
  });
  const origin = routePoints[0];
  const destination = routePoints.at(-1);
  if (!origin || !destination || routePoints.length < 2) {
    return null;
  }
  const params = new URLSearchParams({
    destination: destination.coordinates,
    origin: origin.coordinates,
    travelmode: 'driving'
  });
  setPlaceIdParam(params, 'destination_place_id', destination.placeId);
  setPlaceIdParam(params, 'origin_place_id', origin.placeId);
  const waypoints = routePoints.slice(1, -1);
  if (waypoints.length > 0) {
    params.set('waypoints', waypoints.map(waypoint => waypoint.coordinates).join('|'));
    const waypointPlaceIds = waypoints.map(waypoint => waypoint.placeId).filter((placeId): placeId is string => Boolean(placeId));
    if (waypointPlaceIds.length > 0) {
      params.set('waypoint_place_ids', waypointPlaceIds.join('|'));
    }
  }
  return `https://www.google.com/maps/dir/?api=1&${params.toString()}`;
};
const getDropCoordinates = (drop: GoogleMapsDrop) => {
  const lat = normalizeCoordinate(drop.place?.lat);
  const lng = normalizeCoordinate(drop.place?.lng);
  if (!lat || !lng) {
    return null;
  }
  return `${lat},${lng}`;
};
const normalizeCoordinate = (coordinate?: null | number | string) => {
  if (coordinate === null || coordinate === undefined || coordinate === '') {
    return null;
  }
  return String(coordinate);
};
const setPlaceIdParam = (params: URLSearchParams, key: string, placeId?: null | string) => {
  if (placeId) {
    params.set(key, placeId);
  }
};